import { useEffect, useState } from "react";
import api, { downloadFile, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FileSpreadsheet, Download, Users, Scissors, Activity, Wallet } from "lucide-react";
import { toast } from "sonner";

function ReportCard({ icon: Icon, title, description, onDownload, testid, children }) {
  return (
    <Card className="border-slate-200 shadow-sm hover:border-blue-300 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md flex items-center justify-center bg-blue-50 text-blue-700">
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          </div>
        </div>
        {children && <div className="mt-4">{children}</div>}
        <div className="mt-4 flex gap-2">
          {onDownload.map((b) => (
            <Button key={b.format || b.label} variant={b.primary ? "default" : "outline"} size="sm" onClick={b.onClick} className={b.primary ? "bg-blue-700 hover:bg-blue-800" : ""} data-testid={`${testid}-${b.format}`}>
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {b.label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function RelatoriosPage() {
  const [filters, setFilters] = useState({ date_from: "", date_to: "", status: "", surgeon_id: "" });
  const [surgeons, setSurgeons] = useState([]);
  const [statuses, setStatuses] = useState([]);

  useEffect(() => {
    api.get("/surgeons").then((r) => setSurgeons(r.data || []));
    api.get("/cases/statuses").then((r) => setStatuses(r.data || []));
    // on-mount loader for select options
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildParams() {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }

  async function download(path, fname, extra = {}) {
    try {
      await downloadFile(path, fname, { ...buildParams(), ...extra });
      toast.success("Relatório gerado.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao gerar relatório");
    }
  }

  return (
    <div className="space-y-5" data-testid="relatorios-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Relatórios</h1>
        <p className="text-sm text-slate-500 mt-1">Exportações Excel/CSV consolidadas. Filtros opcionais aplicam aos relatórios de casos.</p>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Cirurgia de</label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="rep-filter-from" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Até</label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="rep-filter-to" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="rep-filter-status">
              <option value="">Todos</option>
              {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Cirurgião</label>
            <select className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white" value={filters.surgeon_id} onChange={(e) => setFilters({ ...filters, surgeon_id: e.target.value })} data-testid="rep-filter-surgeon">
              <option value="">Todos</option>
              {surgeons.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <ReportCard
          icon={Scissors}
          title="Casos cirúrgicos"
          description="Lista completa com dados do paciente, procedimento, financeiro e status."
          testid="report-cases"
          onDownload={[
            { label: "Excel (.xlsx)", format: "xlsx", primary: true, onClick: () => download("/reports/cases.xlsx", "casos.xlsx") },
            { label: "CSV", format: "csv", onClick: () => download("/reports/cases.csv", "casos.csv") },
          ]}
        />
        <ReportCard
          icon={Wallet}
          title="Faturamento consolidado"
          description="Resumo por competência + por cirurgião + todos os casos."
          testid="report-billing"
          onDownload={[
            { label: "Excel (.xlsx)", format: "xlsx", primary: true, onClick: () => download("/reports/billing.xlsx", "faturamento.xlsx") },
          ]}
        />
        <ReportCard
          icon={Activity}
          title="Produção"
          description="Totais, ranking por cirurgião, procedimento, hospital, e mensal."
          testid="report-production"
          onDownload={[
            { label: "Excel (.xlsx)", format: "xlsx", primary: true, onClick: () => download("/reports/production.xlsx", "producao.xlsx") },
          ]}
        />
        <ReportCard
          icon={Users}
          title="Pacientes"
          description="Cadastro completo de pacientes da equipe."
          testid="report-patients"
          onDownload={[
            { label: "Excel (.xlsx)", format: "xlsx", primary: true, onClick: () => download("/reports/patients.xlsx", "pacientes.xlsx") },
          ]}
        />
        <ReportCard
          icon={FileSpreadsheet}
          title="Mapa filtrado (pontual)"
          description="Exporte um mapa filtrado específico acessando a página do mapa e usando o botão de exportação — cada mapa tem seu próprio Excel."
          testid="report-filtered-map"
          onDownload={[]}
        />
      </div>
    </div>
  );
}
